import React from "react";
import {Button} from "@mui/material";
import { proposalEvidence } from "../helpers/teamProposal";
export default function MetricExplanation({
  data,
  teamId,
  model,
  question,
  field,
  recent,
  en,
  compact,
  steps,
  onInspect,
}) {
  const t = (d, e) => (en ? e : d),
    fmt = (n) =>
      Number.isFinite(n)
        ? n.toLocaleString(en ? "en-GB" : "de-DE", { minimumFractionDigits: 3, maximumFractionDigits: 3 })
        : "—";
  const ev = proposalEvidence(data, teamId, model, question, recent, field);
  const labels = {
    should: t("Should Goals · Saison", "Should Goals · season"),
    real: t("Reale Ergebnisse", "Actual results"),
    market: t("Marktrückrechnung", "Market reconstruction"),
    recent: t(`Leistung · letzte ${recent}`, `Performance · last ${recent}`),
    external: t("Externe xGoals", "External xGoals"),
    other: t("Anderes Modell · gespeichert", "Other model · saved"),
    previous: t(
      "Vorsaison · anderer Zeitraum",
      "Previous season · different period"
    ),
  };
  const text = field
    ? t(
        "Offensive bedeutet eigene Tore, Defensive Gegentore pro Spiel. Gesamtsettings mitteln Heim und Auswärts gleich. Historische Werte berücksichtigen die tatsächlich gespielten Gegner.",
        "Attack means goals scored, defence goals conceded per match. Overall settings weight home and away equally. Historical values reflect the opponents actually played."
      )
    : question === "strength"
    ? t(
        "Spielstärke ist Für / Gegen. Über 1 bedeutet mehr eigene Tore als Gegentore, unter 1 weniger. Eine reine Quotientenänderung erhält das Torniveau.",
        "Strength is goals for / against. Above 1 means more goals scored than conceded; below 1 fewer. A ratio-only edit preserves goal level."
      )
    : question === "goals"
    ? t(
        "Torniveau ist Für + Gegen pro Spiel. Eine reine Torniveauänderung erhält den Quotienten.",
        "Goal level is goals for + against per match. Editing only goal level preserves the ratio."
      )
    : question === "home"
    ? t(
        "Heimvorteil ist der Quotient aus offensivem und defensivem Heimfaktor. Beide Faktoren entstehen aus den Heimwerten relativ zu ihren Gesamtwerten.",
        "Home advantage is the ratio of attacking and defensive home factors. Each factor compares the home value with its overall value."
      )
    : t(
        "Je niedriger der Unentschieden-Faktor, desto mehr Remisen bei sonst gleichen Parametern. Das Ergebnismodell verwendet Teamfaktoren; die vorhandene Statistikmodell-Berechnung verwendet den Ligafaktor.",
        "The lower the draw factor, the more draws with other parameters unchanged. The results model uses team factors; the existing statistics-model calculation uses the league factor."
      );
  if (question === "draws")
    return (
      <section className="metric-explanation">
        <h2>{t("Unentschieden-Faktor", "Draw factor")}</h2>
        <p>{text}</p>
        <p>
          {t("Gespeicherter Teamfaktor", "Saved team factor")}:{" "}
          <b>{fmt(data.result_teams?.[teamId]?.draw_factor)}</b> ·{" "}
          {t("Ligafaktor", "League factor")}:{" "}
          {fmt(data.result_parameters?.drawfactor)}
        </p>
        {steps.map((s) => (
          <details key={s.key}>
            <summary>
              {s.title}: {fmt(s.value)} {s.unit}
            </summary>
            <p>{s.compact}</p>
            <p>{s.coverage}</p>
          </details>
        ))}
        <p>
          {t(
            "Der vorhandene Neubewertungsvorschlag dämpft die Differenz aus realen und erwarteten Remisen mit n+140. Seine Wirkung kann gegen einen Ranking-Nachbarn heim/auswärts geschätzt werden.",
            "The existing reassessment dampens actual minus expected draw counts using n+140. Its effect can be estimated against a ranking neighbour, home and away."
          )}
        </p>
      </section>
    );
  if (question === "strength" && !field) {
    const should=ev.sources.find(s=>s.key==="should"), real=ev.sources.find(s=>s.key==="real"), external=ev.sources.find(s=>s.key==="external"), recentSource=ev.sources.find(s=>s.key==="recent");
    const comparable=s=>Number.isFinite(s?.observed)&&Number.isFinite(s?.expected)&&s.count>0;
    const difference=s=>s.observed-s.expected;
    const signed=n=>(n>0?"+":"")+fmt(n);
    const modelName=model==="result"?t("Ergebnismodell","Results model"):t("Statistikmodell","Statistics model");
    return <section className="metric-explanation" aria-label={t("Spielstärke beurteilen","Assess strength")}>
      <h2>{t("Passt die eingestellte Spielstärke zur Leistung?","Does the saved strength fit the performance?")}</h2>
      <div style={{display:"flex",gap:24,flexWrap:"wrap",padding:"12px 0"}}>
        <div><span>{modelName} · {t("aktuell gespeichert","currently saved")}</span><div style={{fontSize:32,fontWeight:750}}>{fmt(ev.currentSaved)}</div></div>
        <div><span>{model==="result"?t("Statistikmodell · gespeichert","Statistics model · saved"):t("Ergebnismodell · gespeichert","Results model · saved")}</span><div style={{fontSize:32,fontWeight:750}}>{fmt(ev.other)}</div></div>
      </div>
      <p>{t("Die gespeicherte Spielstärke ist ein Zukunftswert: Offensive geteilt durch Defensive. 1 bedeutet gleich viele eigene Tore wie Gegentore gegen die Modellreferenz. Sie ist nicht der Tabellenplatz und nicht das reale Torverhältnis.","Saved strength is a future model setting: attack divided by defence. 1 means equal goals scored and conceded against the model reference. It is neither the league position nor the actual goal ratio.")}</p>
      <h3>{t("Was sagen die bisherigen Spiele?","What do the matches show?")}</h3>
      {[should,real,external].filter(Boolean).map(source=><div key={source.key} style={{padding:"10px 12px",marginBottom:8,borderLeft:"3px solid #64b8db",background:"rgba(100,184,219,.07)"}}>
        <strong>{labels[source.key]}</strong>{source.period&&<small style={{display:"block"}}>{source.period}</small>}
        {comparable(source)?<><div>{fmt(source.observed)} {t("beobachtet gegenüber","observed versus")} {fmt(source.expected)} {t("damals erwartet","expected at the time")} · {source.count}/{source.total} {t("Spiele","matches")}</div>
          <div>{source.key==="real"?t("Das reale Torverhältnis liegt","The actual goal ratio is"):t("Das gemessene Leistungsverhältnis liegt","The measured performance ratio is")} {difference(source)>0?t("über","above"):difference(source)<0?t("unter","below"):t("auf","at")} {t("der historischen Erwartung","the historical expectation")} ({signed(difference(source))}).</div></>:<p>{t("Kein vollständiger Vergleich mit der historischen Erwartung verfügbar.","No complete comparison with historical expectations is available.")}</p>}
      </div>)}
      {recentSource?.identical&&<p>{t(`Saison und letzte ${recent} Spiele verwenden hier dieselben Begegnungen. Das ist keine zusätzliche Bestätigung.`,`Season and last ${recent} matches use the same fixtures here. This is not additional confirmation.`)}</p>}
      <h3>{t("Was kannst du daraus entscheiden?","What can you decide from this?")}</h3>
      <p>{comparable(should)&&comparable(real)&&Math.sign(difference(should))!==Math.sign(difference(real))?t("Ergebnis und gemessene Leistung zeigen unterschiedliche Richtungen. Prüfe deshalb zuerst die einzelnen Spiele: Ein schwächeres Ergebnis allein begründet noch keine Abwertung der Spielstärke.","Results and measured performance point in different directions. Inspect the individual matches first: weaker results alone do not justify lowering strength."):t("Vergleiche Leistung, Ergebnisse und externe xGoals gemeinsam. Die Abstände zeigen, welche Quellen für eine Änderung sprechen; sie bestimmen noch keinen automatischen Zielwert.","Compare performance, results and external xGoals together. The differences show which sources support a change; they do not define an automatic target.")}</p>
      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}><Button variant="contained" onClick={onInspect} disabled={!onInspect}>{t("Änderung mit Quellen prüfen","Review a change using sources")}</Button><span>{t("Beibehalten? Mit „Weiter“ zur nächsten Kennzahl.","Keep it? Choose Next to review the next metric.")}</span></div>
      <details style={{marginTop:20}}><summary>{t("Alle Vergleichswerte und Herleitungen öffnen","Open all comparison values and derivations")}</summary><div className="ta-scroll"><table><thead><tr><th>{t("Quelle","Source")}</th><th>{t("Beobachtet / Vergleichswert","Observed / comparison value")}</th><th>{t("Historisch erwartet","Historically expected")}</th><th>{t("Abdeckung","Coverage")}</th></tr></thead><tbody>{ev.sources.map(source=><tr key={source.key}><th>{labels[source.key]}</th><td>{fmt(source.observed??(["other","previous"].includes(source.key)?source.full:null))}</td><td>{fmt(source.expected)}</td><td>{source.count??"—"}{source.total!=null?`/${source.total}`:""}</td></tr>)}</tbody></table></div>{steps.map(s=><div key={s.key}><h4>{s.title}</h4><p>{s.compact} {s.coverage}</p>{s.details?.map((d,i)=><p key={i}>{d}</p>)}</div>)}</details>
    </section>;
  }
  return (
    <section className="metric-explanation">
      <h2>
        {t("Gespeicherter Wert", "Saved value")}: {fmt(ev.saved)}
      </h2>
      <p>{text}</p>
      <p>
        {field || question === "goals"
          ? t("Einheit: Tore pro Spiel.", "Unit: goals per match.")
          : t(
              "Einheit: dimensionsloser Quotient.",
              "Unit: dimensionless ratio."
            )}
      </p>
      {!compact && (
        <p>
          {t(
            "Erwartungen stammen aus den jeweiligen historischen Begegnungen. Verglichen werden je Zeile dieselben Spiele. Für die Anpassung zählt Leistung minus Erwartung; eine gespeicherte Einstellung ist keine historische Erwartung.",
            "Expectations come from the historical fixtures. Each row compares the same matches. Adjustment uses performance minus expectation; a saved setting is not a historical expectation."
          )}
        </p>
      )}
      <div className="ta-scroll">
        <table>
          <thead>
            <tr>
              <th>{t("Vergleich", "Comparison")}</th>
              <th>{t("Wert", "Value")}</th>
              <th>{t("Erwartet · gleiche Spiele", "Expected · same games")}</th>
              <th>{t("Spiele", "Matches")}</th>
            </tr>
          </thead>
          <tbody>
            {ev.sources.map((s) => (
              <tr key={s.key}>
                <th>{labels[s.key]}</th>
                <td>
                  {fmt(
                    s.observed ??
                      (["other", "previous"].includes(s.key) ? s.full : null)
                  )}
                </td>
                <td>{fmt(s.expected)}</td>
                <td>
                  {s.count ?? "—"}
                  {s.total != null ? `/${s.total}` : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!compact && (
        <details>
          <summary>
            {t("Herkunft und Herleitung", "Provenance and derivation")}
          </summary>
          {steps.map((s) => (
            <div key={s.key}>
              <b>
                {s.title}: {fmt(s.value)}
              </b>
              <p>
                {s.compact} {s.coverage}
              </p>
              {s.details?.map((d, i) => (
                <p key={i}>{d}</p>
              ))}
            </div>
          ))}
          <p>
            {t(
              "Should Goals sind unsere Leistungsmessung; externe xGoals eine unabhängige Methode. Fehlende externe Daten werden nicht ersetzt. Eine Abweichung allein beweist keinen Fehler.",
              "Should Goals are our performance measure; external xGoals use an independent method. Missing external values are not replaced. A difference alone does not prove an error."
            )}
          </p>
        </details>
      )}
    </section>
  );
}
